import {
  FakeLink,
  TableHeaderCell,
  TableHeaderRow,
} from "../../../constants/Styles";
import {
  Body,
  PartsTable,
  Filters,
  FiltersHeader,
  TextArea,
  FiltersContainer,
  PaginationContainer,
  SearchHeader,
  NoResults,
  InStockPartsLink,
} from "./PartsTableStyles";
import {
  SearchParameters,
  SearchResponse,
} from "../../../reducers/componentSearch";
import Filter from "./Filter";
import { useAppSelector } from "../../../utils/hooks/store";
import { useUpdateSearch } from "../../../utils/hooks/partSearch";
import _ from "lodash";
import Pagination from "../../Pagination";
import SearchBar from "../SearchBar";
import AdminPanel from "../AdminPanel";
import SearchOptions from "./SearchOptions";

import FilterButtons from "./FilterButtons";
import AppliedFilters from "./AppliedFilters";
import { ReactNode } from "react";
import PartRow from "./PartRow";
import { getIdForPart } from "../../../utils/parts";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../utils/group";
import useDownloadInStockPartsList from "../../../utils/hooks/inventory/inStockPartsList";
import FlashPCBTooltip from "../../common/FlashPCBTooltip";
import InfoIcon from "../../common/FlashPCBTooltip/InfoIcon";

interface PartsTableProps {
  searchResponse: SearchResponse;
  selectedPartId?: string | null;
  setSelectedPartId: (part_id: string | null) => void;
  warnOnNoInventory: boolean;
  noPartsComponent?: ReactNode;
}

export default function PartsTableAndFilters({
  searchResponse,
  selectedPartId,
  setSelectedPartId,
  warnOnNoInventory,
  noPartsComponent,
}: PartsTableProps) {
  const loading = useAppSelector((state) => state.componentSearch.loading);
  const searchParameters = useAppSelector(
    (state) => state.componentSearch.searchParameters as SearchParameters,
  );
  const updateSearch = useUpdateSearch();
  const showRadioButtons = selectedPartId !== undefined;
  const { groups } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const downloadInStockPartsList = useDownloadInStockPartsList();

  const filteredAggregatesForFilters = _.sortBy(
    searchResponse.parameter_aggregates.filter(
      (aggregate) => aggregate.property_aggregate.buckets.length > 0,
    ),
    (aggregate) =>
      aggregate.property_aggregate.buckets.reduce((totalCount, bucket) => {
        if (bucket.count !== null) {
          totalCount += bucket.count;
        }
        return totalCount;
      }, 0),
  ).reverse();

  const filteredAggregatesForTable = filteredAggregatesForFilters.filter(
    (aggregate) => {
      return searchResponse.parts.some((part) => {
        return part.properties.some((partProperty) => {
          return partProperty.parameter.id == aggregate.parameter.id;
        });
      });
    },
  );

  const HEADER_ROWS_INFO = [
    ...(showRadioButtons ? [{ label: "", isRadioButton: true }] : []),
    {
      label: "Manufacturer's Part Number",
    },
    {
      label: "Manufacturer",
    },
    {
      label: "Part Description",
    },
    {
      label: "Unit price",
    },
    {
      label: (
        <FlashPCBTooltip
          content={
            "Availability of parts may decrease under shorter project lead times"
          }
        >
          Quantity Available <InfoIcon />
        </FlashPCBTooltip>
      ),
    },
    {
      label: "In Stock Quantity",
    },
    ...(isAdminOrEngineer
      ? [
          { label: "Last counted total" },
          { label: "Active allocations total" },
          { label: "Spent allocations total" },
        ]
      : []),
    ...filteredAggregatesForTable.map((aggregate) => {
      return { label: aggregate.parameter.name };
    }),
  ];
  return (
    <Body>
      <AdminPanel />
      <Filters>
        <SearchHeader>
          <SearchBar />
          <SearchOptions />
          <InStockPartsLink>
            <FakeLink onClick={() => void downloadInStockPartsList()}>
              Download list of in stock parts
            </FakeLink>
          </InStockPartsLink>
          <FiltersHeader>
            <TextArea>
              {`${searchResponse.total_count.toLocaleString()} Results`}
            </TextArea>
            {(filteredAggregatesForFilters.length > 0 ||
              searchParameters.categoryIds.length > 0) && <FilterButtons />}
          </FiltersHeader>
        </SearchHeader>
        <AppliedFilters />
        {filteredAggregatesForFilters.length > 0 && (
          <FiltersContainer>
            {filteredAggregatesForFilters.map((aggregate) => (
              <Filter
                key={aggregate.property_aggregate.attribute.name}
                parameterAggregate={aggregate}
              />
            ))}
          </FiltersContainer>
        )}
      </Filters>
      {searchResponse.total_count == 0 ? (
        <NoResults>
          No parts match your search
          {noPartsComponent}
        </NoResults>
      ) : (
        <Body>
          <PartsTable>
            <thead>
              <TableHeaderRow>
                {HEADER_ROWS_INFO.map((rowInfo, rowIndex) => {
                  return (
                    <TableHeaderCell
                      key={rowIndex}
                      borderRight={rowInfo.isRadioButton}
                    >
                      <div>{rowInfo.label}</div>
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            </thead>
            <tbody>
              {searchResponse.parts.map((part) => {
                return (
                  <PartRow
                    key={getIdForPart(part)}
                    part={part}
                    selectedPartId={selectedPartId}
                    setSelectedPartId={setSelectedPartId}
                    warnOnNoInventory={warnOnNoInventory}
                    filteredAggregatesForTable={filteredAggregatesForTable}
                    isAdminOrEngineer={isAdminOrEngineer}
                  />
                );
              })}
            </tbody>
          </PartsTable>
        </Body>
      )}
      <PaginationContainer>
        <Pagination
          page={searchParameters.page}
          maxPage={searchResponse.max_page}
          startingIndex={searchResponse.starting_index}
          totalCount={searchResponse.total_count}
          itemsPerPage={searchResponse.inventory_per_page}
          itemsOnPage={searchResponse.parts.length}
          onBackAction={() =>
            updateSearch({
              updateSearchParameters: { page: searchParameters.page - 1 },
            })
          }
          onForwardAction={() =>
            updateSearch({
              updateSearchParameters: { page: searchParameters.page + 1 },
            })
          }
          disabled={loading}
        />
      </PaginationContainer>
    </Body>
  );
}
